import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'

// Form components
import { ImageUploadField } from '@/components/form-components/ImageUploadField'

// Form validation schema
const formSchema = z.object({
  filename: z.string().min(1, 'Filename is required').max(256, 'Filename cannot exceed 256 characters'),
  file: z
    .instanceof(File, { message: 'File is required' })
    .refine((file) => file.size <= 6 * 1024 * 1024, 'File size exceeds the 6 MB limit')
    .refine((file) => file.type.startsWith('image/'), 'Only image files are allowed'),
})

export type TAddImageForm = z.infer<typeof formSchema>

type AddImageFormV2Props = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
}

export const AddImageFormV2 = (props: AddImageFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { postSupportApiAddImage, getSupportApiListImages } = useRestClient()

  const { mutate: addImage, isPending: isAdding } = postSupportApiAddImage.useMutation(undefined, {
    onSuccess: () => {
      getSupportApiListImages.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Image successfully added')
      props.onSubmit?.()
    },
    onError: (error) => {
      console.error('Error in adding image:', error)
      toast.error('Error in adding image')
    },
  })

  const submitHandler = useCallback(
    (data: TAddImageForm) => {
      const formData = new FormData()
      formData.append('filename', data.filename)
      formData.append('file', data.file)

      addImage({ body: formData })
    },
    [addImage]
  )

  const isLoading = isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (props: AddImageFormV2Props & { submitHandler: (data: TAddImageForm) => void }) => {
  const defaultValues = useMemo(() => {
    return {
      filename: '',
      file: undefined as unknown as File,
    }
  }, [])

  const form = useForm<TAddImageForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const currentFile = form.watch('file')

  // Automatically set the filename when a file is selected
  const handleFileChange = (file: File | null) => {
    if (file && !form.getValues('filename')) {
      form.setValue('filename', file.name)
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="filename"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Filename</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter filename" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ImageUploadField
                control={form.control}
                name="file"
                label="Upload Image"
                maxSize={6 * 1024 * 1024}
                acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp']}
                showPreview={true}
                showFileInfo={true}
                onFileChange={handleFileChange}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={!currentFile}>
                Submit
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
